import { useCallback, useState } from 'react'
import type * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Modal from 'LuxKit/Modal/Modal'
import InputChip from 'LuxKit/Chips/InputChip'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

/**
 * The composer truncates its chips to one row; here there is room to read a
 * suggestion in full, so the label wraps instead and the chip grows with it.
 */
const WrappingInputChip = styled(InputChip)`
  max-width: 100%;
  height: auto;
  min-height: ${rem(28)};
  padding: ${rem(4)} ${rem(8)};
  text-align: start;

  > *:not(svg):not(img) {
    white-space: normal;
  }

  > svg {
    flex-shrink: 0;
  }
`

interface Props {
  tags: Array<string>
  onRemoveTag: (tag: string) => void
}

/**
 * The full list of selected tags, behind the composer's "+n more" chip.
 *
 * The composer keeps its tags on a single row so a handful of long suggestions
 * can't eat the chat's height, which means most of them are only reachable
 * here. `showModal` renders a snapshot element, so this list can't follow the
 * composer's props — it holds its own copy and forwards every removal upwards.
 */
function SelectedTagsModal({ tags: initialTags, onRemoveTag }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const [tags, setTags] = useState(initialTags)

  const handleRemoveTag = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const tag = event.currentTarget.dataset.tag
    if (!tag) return

    onRemoveTag(tag)
    const remainingTags = tags.filter((selectedTag) => selectedTag !== tag)
    setTags(remainingTags)
    // Nothing left to group up — the chip that opened this is gone too.
    if (remainingTags.length === 0) modalElement.resolve()
  }, [modalElement, onRemoveTag, tags])

  return (
    <Modal
      title={intl.formatMessage({
        id: 'Cr7Yt6M3k1l4RBoMMNki',
        defaultMessage: 'Selected suggestions',
        description: 'Title of the modal listing every suggestion tag selected in the TripPlanner chat composer',
      })}
    >
      <VerticalSpacer gap={12}>
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage(
            {
              id: 'KEqVTXa-OmUZfxQWK5JK',
              defaultMessage: '{count, plural, one {# suggestion} other {# suggestions}} will be sent with your message. Tap one to remove it.',
              description: 'Explanation above the list of selected suggestion tags in the TripPlanner chat composer modal',
            },
            { count: tags.length },
          )}
        </BodyText>
        <Group direction="horizontal" gap={8} wrap="wrap">
          {tags.map((tag, index) => (
            <WrappingInputChip
              key={`${tag}-${index}`}
              variant="filled"
              data-tag={tag}
              onClick={handleRemoveTag}
              aria-label={intl.formatMessage(
                {
                  id: 'Zb2W-kUOdVBBetSY_w-p',
                  defaultMessage: 'Remove {tag}',
                  description: 'Aria-label for button to remove a selected tag from chat input',
                },
                { tag },
              )}
            >
              {tag}
            </WrappingInputChip>
          ))}
        </Group>
      </VerticalSpacer>
    </Modal>
  )
}

export default SelectedTagsModal
